/**
 * SurfaceRosterRail — a roster, as a rail of peers.
 *
 * ## Roster, not "creators"
 *
 * The people on it are creators only from a PUBLISHER's point of view. An
 * esports organisation's roster is its players; an agency's is its talent; a
 * community's is its members. Naming the component and its type after the
 * first POV to use it would make every other one read as borrowed — the same
 * mistake as naming a build artefact after its first customer.
 *
 * What they all share is the shape: named people, some live right now, some
 * doing something other than what this surface is about. That is a roster.
 *
 * It takes members, not a Drop — the normalisation from a roster payload lives
 * in `dropSpineData.ts`, the one file allowed to speak both vocabularies.
 *
 * ## Selecting one puts it on the stage
 *
 * The rail does not decide what selection MEANS; it reports which member was
 * chosen and the surface decides. That matters because the same rail is used
 * where there is a stage and where there is not — on a page-style surface the
 * caller may navigate instead, and this component should not have an opinion.
 *
 * ## Live is the urgent state, off-title is the quiet one
 *
 * A member streaming a different game gets a marker rather than removal, which
 * is what `filterMode: BADGE` means. Both are decided upstream and arrive as
 * flags — this renders them, it does not compute them, because "is this person
 * on our title" is a question the roster endpoint already answers with the
 * operator's own configured filter.
 */

import React from "react";
import {RosterFieldValues} from "@/widgets/RosterFieldValues";
import {type ResolvedRosterField} from "@/surfaces/rosterFields";
import {SurfaceStrip} from "./SurfaceStrip";

export interface SurfaceRosterMember {
    id: string;
    name: string;
    /** What they are doing: the stream title live, what they last played offline. */
    detail?: string;
    imageUrl?: string;
    live?: boolean;
    /** Streaming something other than this surface's title. */
    offTitle?: boolean;
    /** The roster's declared extra fields, already resolved against this member. */
    fields?: readonly ResolvedRosterField[];
    /**
     * Whether this member has anything to put on the stage. A card with no
     * stream and no clip must not look tappable — the click would be a no-op
     * and the cursor would have promised otherwise.
     */
    watchable?: boolean;
}

export interface SurfaceRosterRailProps {
    members: readonly SurfaceRosterMember[];
    /** The operator's own label for the section — never invented here. */
    kicker?: string;
    /**
     * The same label when the roster IS a section of the page rather than a
     * strip inside one — rendered at display scale like every sibling
     * section. Takes precedence over `kicker`.
     */
    title?: string;
    /** The roster's name, shown opposite the kicker. */
    note?: string;
    onSelect?: (id: string) => void;
    /**
     * How this roster's cards render, from the roster itself. "ART" shows the
     * member's own artwork whole with nothing over it; anything else is the
     * platform card — our frame, the name, the badge, the declared fields.
     */
    cardStyle?: string;
}

export const SurfaceRosterRail: React.FC<SurfaceRosterRailProps> = ({
    members, kicker, title, note, onSelect, cardStyle,
}) => {
    const art = cardStyle === "ART";
    return (
        <SurfaceStrip
            title={title}
            kicker={kicker}
            note={note}
            deck={art}
            cards={members.map(c => ({
                id: c.id,
                artOnly: art,
                title: c.name,
                description: c.detail,
                /*
                 * The roster's declared fields, in the slot already reserved for a
                 * line under the description. Absent on a roster that declares
                 * none, which is the common case.
                 */
                meta: c.fields?.length
                    ? <RosterFieldValues fields={c.fields} />
                    : undefined,
                imageUrl: c.imageUrl,
                /*
                 * Live outranks off-title: someone streaming the wrong game while
                 * live is still live, and the red badge is the one that earns the
                 * corner. Only one badge fits, so the order is the decision.
                 */
                badge: c.live
                    ? {label: "Live", kind: "live" as const}
                    : c.offTitle
                        ? {label: "Off title", kind: "soon" as const}
                        : undefined,
                onClick: onSelect && c.watchable ? () => onSelect(c.id) : undefined,
            }))}
        />
    );
};

export default SurfaceRosterRail;
